import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchMetrics,
  fetchLiveWorkers,
  fetchSystemWorkers,
  type SystemMetrics,
  type LiveWorkers,
  type SystemWorker,
} from 'api/systemApi';

const POLL_MS = 2500;

/**
 * Drives the system panel modal: polls /metrics (and the live worker list) on
 * an interval, but ONLY while `open` is true — the interval is torn down on
 * close/unmount so a hidden modal never hits the backend. System-worker rows are
 * refetched on demand after a mutation via `refreshWorkers`.
 */
export function useSystemPanel(open: boolean) {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [liveWorkers, setLiveWorkers] = useState<LiveWorkers | null>(null);
  const [systemWorkers, setSystemWorkers] = useState<SystemWorker[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refreshWorkers = useCallback(async () => {
    try {
      const [sys, live] = await Promise.all([fetchSystemWorkers(), fetchLiveWorkers()]);
      if (!mounted.current) return;
      setSystemWorkers(sys);
      setLiveWorkers(live);
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const ctrl = new AbortController();

    const pollMetrics = async () => {
      try {
        const m = await fetchMetrics(ctrl.signal);
        if (!cancelled && mounted.current) {
          setMetrics(m);
          setError(null);
        }
      } catch (e) {
        if (!cancelled && mounted.current && (e as Error)?.name !== 'AbortError') {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    };

    // Initial load: metrics + both worker lists, then poll metrics on interval.
    void pollMetrics();
    void refreshWorkers();
    const id = window.setInterval(pollMetrics, POLL_MS);

    return () => {
      cancelled = true;
      ctrl.abort();
      window.clearInterval(id);
    };
  }, [open, refreshWorkers]);

  return { metrics, liveWorkers, systemWorkers, error, refreshWorkers };
}
